import { createSelector } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { BillingVersion } from 'common/billing/constants/AccountBillingInfo'
import { BillingActions } from 'common/billing/constants/BillingActions'
import { ReasonListLabels } from 'common/billing/constants/reasonListLabels'
import { SubscriptionAction } from 'common/billing/constants/SubscriptionAction'
import { ProInfoBase } from 'common/billing/entities/pro/interfaces'
import { getBillingProInfo } from 'common/billing/entities/pro/selectors'
import { CancellationReasonType } from 'common/billing/interfaces/cancellationReasons'
import * as AccountModel from 'common/core/models/Account/helpers'
import {
  getAccountFlags,
  getAsyncBillingActions,
  getCurrentAccount,
  isTrialAvailable,
} from 'common/core/selectors/appSelectors'
import { CancellationReason } from 'shared/api/requests/billing/schemas'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

export const getCancelSubscriptionModalState = (state: RootState) =>
  state.billing.cancelSubscriptionModal.isOpen

export type SubscriptionInfo =
  | (ProInfoBase & {
      isPro: boolean
      isFree: boolean
      isTrial: boolean
      isUkraineReliefProgram: boolean
      isExpired: boolean
      isPendingCancellation: boolean
    })
  | Record<string, never>

export const getSubscriptionInfo = createSelector(
  [getBillingProInfo, getAccountFlags],
  (pro, flags): SubscriptionInfo => {
    const { status, billing_action, plan, billing_version: billingVersion } = pro

    if (!status) {
      return {}
    }

    const isPro = AccountModel.isProOrTrialStatus(status)
    const isTrialStatus = AccountModel.isTrialStatus(status)

    const isUkraineReliefProgram =
      Boolean(flags?.[AccountFlag.UKRAINE_2022_SUPPORT]) &&
      billingVersion === BillingVersion.FLEX &&
      plan === 'total_monthly'

    return {
      ...(pro as ProInfoBase),
      isPro,
      isFree: AccountModel.isFreeStatus(status),
      isTrial: isTrialStatus,
      isUkraineReliefProgram,
      isExpired: AccountModel.isExpiredStatus(status),
      isPendingCancellation: isPro && billing_action === BillingActions.BACK_TO_PRO,
    }
  },
)

export const getSubscriptionAction = createSelector(
  [getCurrentAccount, getAsyncBillingActions, isTrialAvailable],
  (currentAccount, asyncNextActions, trialAvailable): SubscriptionAction => {
    if (asyncNextActions?.action_type || asyncNextActions?.next_action_redirect) {
      return SubscriptionAction.CONTINUE_CHECKOUT
    }

    if (trialAvailable) {
      return SubscriptionAction.ACTIVATE_TRIAL
    }

    if (AccountModel.isExpired(currentAccount)) {
      return SubscriptionAction.RENEW
    }

    if (currentAccount.products?.has_automation && currentAccount.products?.has_inbox) {
      return SubscriptionAction.NONE
    }

    if (AccountModel.isProOrTrial(currentAccount)) {
      return SubscriptionAction.CHANGE_PLAN
    }

    return SubscriptionAction.UPGRADE
  },
)

export const getIsSubscriptionActionEqual = createSelector(
  [
    getSubscriptionAction,
    (state: RootState, requestedStatus: SubscriptionAction) => requestedStatus,
  ],
  (currentStatus, requestedStatus) => currentStatus === requestedStatus,
)

const getCancellationReasonsRaw = createSelector(
  getBillingProInfo,
  (pro) => pro.cancellationReasonsList || [],
)

export const getCancellationReasonsList = createSelector(
  getCancellationReasonsRaw,
  (reasons): CancellationReason[] =>
    reasons.map((reason) => ({
      ...reason,
      label:
        reason.id in ReasonListLabels ? l.getString(ReasonListLabels[reason.id]) : reason.label,
    })),
)

export const getRootCancellationReasonsList = createSelector(
  getCancellationReasonsList,
  (reasons): CancellationReason[] => reasons.filter((reason) => reason.parent === null),
)

export const getNestedCancelationReasons = createSelector(
  getCancellationReasonsList,
  (state: RootState, id?: CancellationReasonType | null) => id,
  (reasons, id): CancellationReason[] =>
    id ? reasons.filter((reason) => reason.parent === id) : [],
)

export const getHasChildReasons = createSelector(
  (state: RootState, id?: CancellationReasonType | null) => getNestedCancelationReasons(state, id),
  (children) => Boolean(children.length),
)
